'use client'

import React from 'react'
import { usePathname } from 'next/navigation'
import { Cart } from 'solar-icons'
import {
  useCart,
  useCheckout,
  useSearchParams,
  useSelectedCustomer,
} from '@/lib/hooks'
import { cn } from '@/lib/utils'

import {
  BarcodeIndicator,
  Button,
  Clock,
  FullScreenButton,
  NetworkIndicator,
} from '@/components/ui'

interface ToolbarProps extends React.ComponentPropsWithoutRef<'div'> {}

export const Toolbar = ({ className, ...restProps }: ToolbarProps) => {
  const pathname = usePathname()
  const isKot = pathname.startsWith('/kot')
  const isOrderPreview = pathname.startsWith('/order-preview')
  const isOnboarding = pathname.startsWith('/onboard')

  const { showMobileCart, isCheckout, setSearchParams } = useSearchParams()
  const { cart } = useCart()
  const { setAsGuestCustomer } = useSelectedCustomer()
  const { resetCheckout } = useCheckout()

  const handleCartClick = React.useCallback(() => {
    // If closing the mobile cart (showMobileCart is currently true)
    if (showMobileCart) {
      setAsGuestCustomer()
      resetCheckout()
    }

    setSearchParams({
      showMobileCart: !showMobileCart,
      isCheckout: showMobileCart ? false : isCheckout,
    })
  }, [
    showMobileCart,
    isCheckout,
    setSearchParams,
    setAsGuestCustomer,
    resetCheckout,
  ])

  return (
    <div
      className={cn(
        'flex items-center justify-end gap-4 text-sm font-medium',
        {
          'w-full': isKot,
        },
        className,
      )}
      {...restProps}
    >
      <Clock />

      <div className="flex items-center gap-1">
        <div className="flex items-center gap-3">
          {!isKot && !isOrderPreview && !isOnboarding && <BarcodeIndicator />}
          <NetworkIndicator />
        </div>
        <span className="hidden lg:flex">
          <FullScreenButton />
        </span>

        {!isKot && (
          <Button
            variant="ghost"
            size="sm"
            className="relative lg:hidden"
            onClick={handleCartClick}
            aria-label="Cart"
          >
            <span className="absolute -top-0 right-1 flex size-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
              {cart?.cart_items?.length}
            </span>
            <Cart className="size-5" strokeWidth={1.5} />
          </Button>
        )}
      </div>
    </div>
  )
}
